import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getExamSubjects, getExamBlueprint, getExam, getExamsForStudent, getRegion, type QuestionType, type PaperBlueprint } from '../config/regionConfig';
import {
  getNextPracticeSet, generatePracticeSet, pollForPracticeSet,
  getCompletedPaperCount, getCompletedSessionCount,
  type PracticeSegment,
} from '../services/practiceTestService';
import { ArrowLeft, Play, Clock, FileQuestion, Award, AlertTriangle, Sparkles, Users, BookOpen } from 'lucide-react';

const MAX_PAPERS = 20;

const TYPE_LABELS: Record<QuestionType, string> = {
  mcq_single: 'MCQ',
  mcq_multi: 'Multi-Select MCQ',
  numerical: 'Numerical Value',
  assertion_reasoning: 'Assertion-Reasoning',
  short_answer: 'Short Answer',
  long_answer: 'Long Answer',
  case_based: 'Case-Based',
};

type Phase = 'idle' | 'checking' | 'generating' | 'polling';

function sectionRow(section: PaperBlueprint['sections'][number]) {
  return (
    <div key={section.label + section.title} className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2">
        <span className="font-medium text-ocean-deep">{section.label}</span>
        <span className="text-gray-500">{section.title}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="px-2 py-0.5 bg-orange-50 text-coral-warm rounded-full font-medium">
          {TYPE_LABELS[section.questionType]}
        </span>
        <span>{section.count} × {section.marksPerQuestion}m</span>
        {section.attemptLimit !== undefined && (
          <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium">
            attempt {section.attemptLimit}/{section.count}
          </span>
        )}
        {section.negativeMarks > 0 && (
          <span className="flex items-center gap-0.5 text-red-500">
            <AlertTriangle className="w-3 h-3" />
            -{section.negativeMarks}
          </span>
        )}
      </div>
    </div>
  );
}

export default function PracticeTestStart() {
  const { examType } = useParams<{ examType: string }>();
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedSubject, setSelectedSubject] = useState('');
  const [showTimer, setShowTimer] = useState(true);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState('');
  const [paperCount, setPaperCount] = useState<number | null>(null);
  const [sessionCount, setSessionCount] = useState<number | null>(null);

  const decodedExam = examType ? decodeURIComponent(examType) : '';
  const examConfig = getExam(decodedExam);
  const continuousFlow = examConfig?.continuousFlow;
  const subjects = getExamSubjects(decodedExam);

  // Board exams: each subject has its own 20-paper cap — refetch whenever the subject changes.
  useEffect(() => {
    if (!session || continuousFlow) return;
    const subjectForCount = selectedSubject || subjects[0] || decodedExam;
    let cancelled = false;
    getCompletedPaperCount(session.student_id, decodedExam, subjectForCount).then(count => {
      if (!cancelled) setPaperCount(count);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, decodedExam, selectedSubject, continuousFlow]);

  // Continuous-flow exams: one 20-sitting cap covering every subject together.
  useEffect(() => {
    if (!session || !continuousFlow) return;
    let cancelled = false;
    getCompletedSessionCount(session.student_id, decodedExam, subjects.length).then(count => {
      if (!cancelled) setSessionCount(count);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, decodedExam, continuousFlow]);

  // Phase 8 Exam Prep orchestration's "Real Exam Questions"/"Start Simulation"
  // rows link here with ?subject=... to pre-select the subject the student
  // was already working on. Guarded off for continuous-flow exams (JEE/NEET)
  // since those cover every subject in one pooled sitting -- there's no
  // single-subject picker to pre-select for. Matched loosely (case-insensitive
  // prefix/substring), not exact-equals: the concept catalog's subject_name
  // (e.g. "Math") doesn't always match the exam's canonical subject name
  // (e.g. "Mathematics") one-for-one.
  useEffect(() => {
    if (continuousFlow) return;
    const requested = new URLSearchParams(location.search).get('subject');
    if (!requested) return;
    const rLower = requested.toLowerCase();
    const match = subjects.find((s) => {
      const sLower = s.toLowerCase();
      return sLower === rLower || sLower.startsWith(rLower) || rLower.startsWith(sLower);
    });
    if (match) {
      setSelectedSubject(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, decodedExam, continuousFlow]);

  if (isLoading || !session || !examType) return null;

  // Re-check grade eligibility here too — the hub page filters by grade, but
  // a student can deep-link straight to this URL bypassing that filter.
  const isEligible = getExamsForStudent(session.grade_level, session.board).some(e => e.name === decodedExam);

  const isStateBoardExam = decodedExam === 'State Board 12';

  // Continuous-flow exams (SAT, JEE Main/Advanced, NEET) cover every subject in one sitting —
  // there is no subject picker; `subjects` is the fixed sequence, not a menu of choices.
  const previewSegments = continuousFlow
    ? subjects.map(subject => ({ subject, bp: getExamBlueprint(decodedExam, subject)! }))
    : null;

  // State Board 12: per-state blueprint lookup (preview only — the server
  // independently re-derives state_board and is authoritative for what
  // actually gets generated). Other exams: subject-specific blueprint
  // lookup (e.g. SAT has different blueprints for R&W vs Math).
  const blueprint = isStateBoardExam
    ? getExamBlueprint(decodedExam, session.state_board)
    : getExamBlueprint(decodedExam, selectedSubject || undefined);
  const effectiveSubject = selectedSubject || subjects[0] || decodedExam;

  const busy = phase !== 'idle';

  const combinedTotals = previewSegments
    ? {
        marks: previewSegments.reduce((s, seg) => s + seg.bp.totalMarks, 0),
        minutes: previewSegments.reduce((s, seg) => s + seg.bp.durationMinutes, 0)
          + (examConfig?.breakBetweenSubjectsMinutes ?? 0),
        questions: previewSegments.reduce((s, seg) => s + seg.bp.sections.reduce((ss, sec) => ss + sec.count, 0), 0),
      }
    : null;

  async function handleStartTest() {
    if (continuousFlow) return handleStartContinuousTest();
    if (!session) return;
    setError('');
    setPhase('checking');

    // 1. Look for an unattempted ready set
    const set = await getNextPracticeSet(
      session.student_id,
      decodedExam,
      session.grade_level,
      getRegion(),
      selectedSubject || undefined
    );

    if (set) {
      navigate(`/practice-test/attempt/${set.set_id}`, {
        state: { showTimer, examType: decodedExam, subject: selectedSubject, blueprint },
      });
      return;
    }

    // 2. No ready set — trigger AI generation automatically.
    //    The edge function inserts a "generating" placeholder row first.
    //    If another student already triggered generation the function returns
    //    immediately with { set_id, status: 'generating' } so we poll.
    setPhase('generating');

    const result = await generatePracticeSet(
      decodedExam,
      session.grade_level,
      effectiveSubject,
      session.student_id,
      getRegion(),
      blueprint || undefined
    );

    if (!result) {
      setError('Could not generate a practice set. Please try again.');
      setPhase('idle');
      return;
    }

    if (result.paperLimitReached) {
      setError(result.error || `You have completed the maximum of ${MAX_PAPERS} practice papers for this subject. You can retake previous papers from your archive.`);
      setPhase('idle');
      return;
    }

    if (result.status === 'generating') {
      // Another student is generating this exact set — switch to polling mode
      setPhase('polling');
      const ready = await pollForPracticeSet(result.set_id);
      if (!ready) {
        setError('The test took too long to prepare. Please try again in a moment.');
        setPhase('idle');
        return;
      }
      navigate(`/practice-test/attempt/${result.set_id}`, {
        state: { showTimer, examType: decodedExam, subject: selectedSubject, blueprint },
      });
      return;
    }

    // Generation complete — navigate directly
    navigate(`/practice-test/attempt/${result.set_id}`, {
      state: { showTimer, examType: decodedExam, subject: selectedSubject, blueprint },
    });
  }

  // Builds a practice set for every subject in the sequence (no student choice involved),
  // then hands the whole sequence to PracticeTestTaking as one continuous session.
  async function handleStartContinuousTest() {
    if (!session || !examConfig) return;
    setError('');

    // One full exam simulation = every subject together; capped at MAX_PAPERS sittings total.
    if ((sessionCount ?? 0) >= MAX_PAPERS) {
      setError(`You have completed the maximum of ${MAX_PAPERS} full ${decodedExam} simulations. You can retake previous papers from your archive.`);
      return;
    }

    const sessionId = crypto.randomUUID();
    const segments: PracticeSegment[] = [];

    for (const subject of subjects) {
      const segBlueprint = getExamBlueprint(decodedExam, subject);
      setPhase('checking');

      const existing = await getNextPracticeSet(
        session.student_id,
        decodedExam,
        session.grade_level,
        getRegion(),
        subject
      );

      if (existing) {
        segments.push({ subject, setId: existing.set_id, blueprint: segBlueprint! });
        continue;
      }

      setPhase('generating');
      const result = await generatePracticeSet(
        decodedExam,
        session.grade_level,
        subject,
        session.student_id,
        getRegion(),
        segBlueprint || undefined
      );

      if (!result) {
        setError(`Could not generate the ${subject} section. Please try again.`);
        setPhase('idle');
        return;
      }

      if (result.paperLimitReached) {
        setError(result.error || `Could not generate the ${subject} section right now.`);
        setPhase('idle');
        return;
      }

      let readySetId = result.set_id;
      if (result.status === 'generating') {
        setPhase('polling');
        const ready = await pollForPracticeSet(result.set_id);
        if (!ready) {
          setError(`The ${subject} section took too long to prepare. Please try again in a moment.`);
          setPhase('idle');
          return;
        }
        readySetId = result.set_id;
      }

      segments.push({ subject, setId: readySetId, blueprint: segBlueprint! });
    }

    navigate(`/practice-test/attempt/${segments[0].setId}`, {
      state: {
        showTimer,
        examType: decodedExam,
        segments,
        segmentIndex: 0,
        continuousMode: continuousFlow,
        breakMinutes: examConfig.breakBetweenSubjectsMinutes,
        sessionId,
      },
    });
  }

  return (
    <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex items-center justify-between mb-6">
        <Link to="/practice-test" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-ocean-deep transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to exams
        </Link>
        <Link to="/practice-test/archive" className="inline-flex items-center gap-1.5 text-sm text-coral-warm hover:text-orange-600 font-medium transition-colors">
          <BookOpen className="w-4 h-4" />
          My Past Papers
        </Link>
      </div>

      <div className="glass-card rounded-xl p-6 sm:p-8 shadow-lg animate-fade-in-up">
        <h1 className="text-xl sm:text-2xl font-bold text-ocean-deep mb-1">
          {decodedExam} — Exam Simulator
        </h1>
        <p className="text-gray-500 text-sm mb-6">
          Grade {session.grade_level}
          {combinedTotals
            ? ` — ${combinedTotals.marks} marks, ${combinedTotals.minutes} min — one continuous sitting`
            : blueprint
              ? ` — ${blueprint.totalMarks} marks, ${blueprint.durationMinutes} min${blueprint.readingTimeMinutes ? ` + ${blueprint.readingTimeMinutes} min reading` : ''}`
              : ' — 10 questions'}
        </p>

        {!isEligible && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-800">This exam isn't available for your grade level.</p>
          </div>
        )}

        {/* Continuous-flow paper structure: every subject shown back to back, no picker */}
        {previewSegments && combinedTotals && (
          <div className="mb-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="text-sm font-semibold text-ocean-deep mb-3">Paper Structure</h3>
            <div className="mb-3 p-2.5 bg-orange-50 border border-orange-200 rounded-lg text-xs text-coral-warm flex items-center gap-2">
              <FileQuestion className="w-3.5 h-3.5 flex-shrink-0" />
              {continuousFlow === 'pooled'
                ? `Covers ${subjects.join(', ')} in one continuous sitting — free navigation across subjects, single timer`
                : `Runs ${subjects.join(' then ')} in sequence — each under its own timer`}
            </div>
            {previewSegments.map((seg, i) => (
              <div key={seg.subject} className={i > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-ocean-deep">{seg.subject}</span>
                  <span className="text-xs text-gray-500">{seg.bp.totalMarks} marks, {seg.bp.durationMinutes} min</span>
                </div>
                <div className="space-y-2">
                  {seg.bp.sections.map(sectionRow)}
                </div>
                {continuousFlow === 'segmented' && examConfig?.breakBetweenSubjectsMinutes && i < previewSegments.length - 1 && (
                  <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                    Mandatory {examConfig.breakBetweenSubjectsMinutes}-min break before {previewSegments[i + 1].subject} (real exam format)
                  </div>
                )}
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="font-medium text-ocean-deep">Total</span>
              <div className="flex items-center gap-3 text-gray-600">
                <span className="flex items-center gap-1"><FileQuestion className="w-3.5 h-3.5" />{combinedTotals.questions} questions</span>
                <span className="flex items-center gap-1"><Award className="w-3.5 h-3.5" />{combinedTotals.marks} marks</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{combinedTotals.minutes} min</span>
              </div>
            </div>
          </div>
        )}

        {/* Blueprint section breakdown (single-subject exams only) */}
        {!previewSegments && blueprint && (
          <div className="mb-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="text-sm font-semibold text-ocean-deep mb-3">Paper Structure</h3>
            {blueprint.readingTimeMinutes && (
              <div className="mb-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                {blueprint.readingTimeMinutes} min reading time before exam starts — no writing allowed during reading
              </div>
            )}
            <div className="space-y-2">
              {blueprint.sections.map(sectionRow)}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="font-medium text-ocean-deep">Total</span>
              <div className="flex items-center gap-3 text-gray-600">
                <span className="flex items-center gap-1"><FileQuestion className="w-3.5 h-3.5" />{blueprint.sections.reduce((s, sec) => s + sec.count, 0)} questions</span>
                <span className="flex items-center gap-1"><Award className="w-3.5 h-3.5" />{blueprint.totalMarks} marks</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{blueprint.durationMinutes} min</span>
              </div>
            </div>
          </div>
        )}

        {/* Paper attempt counter */}
        {continuousFlow && sessionCount !== null && (
          <div className={`mb-6 p-3 rounded-xl text-sm flex items-center justify-between ${sessionCount >= MAX_PAPERS ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-gray-50 border border-gray-200 text-gray-600'}`}>
            <span>Full exam simulations used</span>
            <span className="font-bold">{sessionCount}/{MAX_PAPERS}</span>
          </div>
        )}
        {!continuousFlow && paperCount !== null && (subjects.length <= 1 || !!selectedSubject) && (
          <div className={`mb-6 p-3 rounded-xl text-sm flex items-center justify-between ${paperCount >= MAX_PAPERS ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-gray-50 border border-gray-200 text-gray-600'}`}>
            <span>{effectiveSubject} papers completed</span>
            <span className="font-bold">{paperCount}/{MAX_PAPERS}</span>
          </div>
        )}

        {/* Subject selector — only for exams where subjects are genuinely independent papers */}
        {!continuousFlow && subjects.length > 1 && (
          <div className="mb-6">
            <label className="block text-sm font-semibold text-ocean-deep mb-2">
              Select Subject
            </label>
            <div className="grid grid-cols-2 gap-2">
              {subjects.map(subject => (
                <button
                  key={subject}
                  onClick={() => setSelectedSubject(subject)}
                  disabled={busy}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border-2 disabled:opacity-50 ${
                    selectedSubject === subject
                      ? 'border-coral-warm bg-orange-50 text-coral-warm'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {subject}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Timer toggle */}
        <div className="mb-6 flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
          <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-ocean-deep">Show Timer</p>
            <p className="text-xs text-gray-500">Time is always tracked in the background for analytics</p>
          </div>
          <button
            onClick={() => setShowTimer(!showTimer)}
            disabled={busy}
            className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${showTimer ? 'bg-coral-warm' : 'bg-gray-300'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${showTimer ? 'translate-x-5' : ''}`} />
          </button>
        </div>

        {/* Status banners — shown only while busy */}
        {phase === 'checking' && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-xl flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-coral-warm flex-shrink-0" />
            <p className="text-sm text-gray-700">Checking for available practice sets…</p>
          </div>
        )}

        {phase === 'generating' && (
          <div className="mb-6 p-5 bg-orange-50 border border-orange-200 rounded-xl text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-coral-warm" />
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-coral-warm" />
            </div>
            <p className="text-sm font-medium text-orange-800">AI is preparing your practice paper…</p>
            <p className="text-xs text-coral-warm mt-1">Loading…</p>
          </div>
        )}

        {phase === 'polling' && (
          <div className="mb-6 p-5 bg-purple-50 border border-purple-200 rounded-xl text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Users className="w-5 h-5 text-purple-500" />
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500" />
            </div>
            <p className="text-sm font-medium text-purple-800">Your test is being prepared — almost ready!</p>
            <p className="text-xs text-purple-600 mt-1">Another student triggered generation first. You'll start as soon as it's ready.</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Start button */}
        <button
          onClick={handleStartTest}
          disabled={
            busy ||
            !isEligible ||
            (!continuousFlow && subjects.length > 1 && !selectedSubject) ||
            (continuousFlow ? (sessionCount ?? 0) >= MAX_PAPERS : (paperCount ?? 0) >= MAX_PAPERS)
          }
          className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-xl font-bold text-base shadow-lg shadow-indigo-500/30 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
        >
          {busy ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
          ) : (
            <>
              <Play className="w-5 h-5" />
              Start Test
            </>
          )}
        </button>
      </div>
    </main>
  );
}
